import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { IconX } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Menu } from '@/components/navigation/Menu'
import { setSelectedNode, setDrawerActiveTab } from '@/reducers/voiceAgents/voiceAgents.redux'
import { getNodeById, getSelectedNode } from '@/reducers/voiceAgents/voiceAgents.helpers'

import { NODE_ICON_PATHS, NODE_TYPE_LABEL } from './canvasHelpers'
import { getVisibleTabs, TAB_LABELS } from './drawerHelpers'
import { TARGET_TYPE_BY_TAB, countOpenRequestsForTarget } from './changeRequestHelpers'

import NodeChips from './components/NodeChips'
import DrawerCommentsPanel from './components/DrawerCommentsPanel'
import PromptTab from './components/PromptTab'
import ModelTab from './components/ModelTab'
import HandoffsTab from './components/HandoffsTab'
import ToolsTab from './components/ToolsTab'
import VariablesTab from './components/VariablesTab'

// =============================================================================
// NodeDrawer — right-side details for the selected node. Always
// renders as a permanent right column (NOT the shared overlay <Drawer>),
// matching the design mock's layout intent.
//
// Tab visibility per node type:
//   entry   → no tabs (just the header)
//   greeting / prompt → Prompt + Tools + Variables
//   subagent          → Prompt + LLM settings + Handoffs + Tools + Variables
//   decision          → "Branch conditions" view in the Prompt slot
//   tool / exit       → Tool config view in the Prompt slot
// =============================================================================

// Tabs that support the content ↔ comments toggle (DrawerCommentsPanel).
const COMMENTABLE_TABS = Object.keys(TARGET_TYPE_BY_TAB)

const DecisionView = ({ node }) => {
  const conditions = node?.config?.conditions || []
  return (
    <div className="voice-agents__drawer-tab" data-testid="drawer-tab-decision">
      <h4 className="voice-agents__drawer-section-title">Branch conditions</h4>
      {isEmpty(conditions) ? (
        <p className="voice-agents__drawer-tab-hint">No conditions configured.</p>
      ) : (
        <ul className="voice-agents__decision-list">
          {conditions.map((condition) => (
            <li key={condition.label} className="voice-agents__decision-row">
              <div className="voice-agents__decision-row-label">{condition.label}</div>
              <div className="voice-agents__decision-row-target">
                → <code>{condition.target}</code>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const ToolConfigView = ({ node }) => {
  const config = node?.config || {}
  return (
    <div className="voice-agents__drawer-tab" data-testid="drawer-tab-tool-config">
      <div className="voice-agents__drawer-row">
        <span className="voice-agents__drawer-row-label">Name</span>
        <span className="voice-agents__drawer-row-value">
          <code>{config.name || node?.id}</code>
        </span>
      </div>
      <div className="voice-agents__drawer-row">
        <span className="voice-agents__drawer-row-label">Description</span>
        <span className="voice-agents__drawer-row-value">{config.description || '—'}</span>
      </div>
      {!isEmpty(config.params) && (
        <div>
          <div className="voice-agents__drawer-section-title">Parameters</div>
          <ul className="voice-agents__tool-row-params">
            {config.params.map((param) => (
              <li key={param.name}>
                <code>{param.name}</code>
                {param.type ? <span> ({param.type})</span> : null}
                {param.description ? <span> — {param.description}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}
      {!isEmpty(config.usedBy) && (
        <div>
          <div className="voice-agents__drawer-section-title">Called from</div>
          <div className="voice-agents__used-by-chips">
            {config.usedBy.map((id) => (
              <code key={id} className="voice-agents__used-by-chip">
                {id}
              </code>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Body for the custom (non-tabbed) node types; tabbed nodes fall through to
// the active tab's content. Module-level so it doesn't inflate NodeDrawer's
// cognitive complexity.
const renderNodeBody = (node, renderTabContent) => {
  if (node.type === 'decision') {
    return <DecisionView node={node} />
  }
  if (node.type === 'tool' || node.type === 'exit') {
    return <ToolConfigView node={node} />
  }
  if (node.type === 'entry') {
    return (
      <p className="voice-agents__drawer-tab-hint">
        This is the call-start node — no editable configuration.
      </p>
    )
  }
  return renderTabContent()
}

// `nodeId` pins the drawer to one node instead of following the selection.
// NodeDrawerDock passes the node it was showing when you deselected, so the
// panel still has content to slide out with — following the selection there
// would empty the drawer on the first frame of the exit and animate a blank
// box. Omitted, it tracks `selectedNodeId` as it always has.
const NodeDrawer = ({ nodeId }) => {
  const dispatch = useDispatch()
  const node = useSelector((state) =>
    nodeId ? getNodeById(state.voiceAgents, nodeId) : getSelectedNode(state.voiceAgents)
  )
  const drawer = useSelector((state) => state.voiceAgents.drawer)
  const changeRequestItems = useSelector((state) => state.voiceAgents.changeRequests.items)

  // Build the SVG markup once per node-type rather than on every drawer
  // render (every store update — popover open/close, loading flips, etc —
  // triggered a fresh template-string parse + dangerouslySetInnerHTML
  // rebuild before). Hook stays above the early return so the hook order is
  // stable when `node` flips between null and non-null.
  const heroIconHtml = useMemo(() => {
    if (!node) {
      return ''
    }
    const iconPath = NODE_ICON_PATHS[node.type] || ''
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${iconPath}</svg>`
  }, [node?.type])

  // Defensive null-return — VoiceAgentsPage gates the drawer mount on
  // selectedNodeId so the canvas can claim the freed width when nothing is
  // selected. This branch only fires if something selects-then-deselects
  // a node mid-render before the parent re-renders.
  if (!node) {
    return null
  }

  const isCustomView =
    node.type === 'entry' ||
    node.type === 'decision' ||
    node.type === 'tool' ||
    node.type === 'exit'
  const tabs = getVisibleTabs(node)
  const activeTab = tabs.includes(drawer.activeTab) ? drawer.activeTab : tabs[0] || null

  const renderTabContent = () => {
    switch (activeTab) {
      case 'prompt':
        return <PromptTab key={node.id} node={node} />
      case 'model':
        return <ModelTab node={node} />
      case 'handoffs':
        return <HandoffsTab node={node} />
      case 'tools':
        return <ToolsTab node={node} />
      case 'variables':
        return <VariablesTab node={node} />
      default:
        return null
    }
  }

  const heroTypeLabel =
    node.type === 'subagent' && node.isMaster
      ? 'Entry agent · Sub-agent'
      : NODE_TYPE_LABEL[node.type] || node.type

  return (
    <aside className="voice-agents__drawer" data-testid="node-drawer" aria-label="Node detail">
      <div
        className="voice-agents__drawer-header"
        data-node-type={node.type}
        data-testid="drawer-header"
      >
        <div className="voice-agents__drawer-hero">
          <div
            className="voice-agents__drawer-hero-icon"
            aria-hidden="true"
            // Static SVG path string from canvasHelpers — memoized per node.type.
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: heroIconHtml }}
          />
          <div className="voice-agents__drawer-hero-text">
            <span className="voice-agents__drawer-hero-kicker">{heroTypeLabel}</span>
            <h2 className="voice-agents__drawer-hero-title">{node.title}</h2>
          </div>
        </div>
        <div className="voice-agents__drawer-header-actions">
          <Button
            basic
            square
            small
            onClick={() => dispatch(setSelectedNode(null))}
            aria-label="Close drawer"
            data-testid="drawer-close"
          >
            <IconX />
          </Button>
        </div>
      </div>

      {(node.config?.description || node.desc) && (
        <p className="voice-agents__drawer-desc">{node.config?.description || node.desc}</p>
      )}

      <div className="voice-agents__drawer-chips-row">
        <NodeChips
          node={node}
          nodeId={node.id}
          onChipClick={(_nodeId, tabKey) => dispatch(setDrawerActiveTab(tabKey))}
          dataTestIdPrefix={`drawer-${node.id}`}
        />
      </div>

      {!isCustomView && !isEmpty(tabs) && (
        <div className="voice-agents__drawer-tabs" data-testid="drawer-tabs">
          <Menu
            items={tabs.map((key) => {
              const tabCommentCount = TARGET_TYPE_BY_TAB[key]
                ? countOpenRequestsForTarget(changeRequestItems, TARGET_TYPE_BY_TAB[key], node.id)
                : 0
              return {
                accessor: key,
                label: TAB_LABELS[key],
                active: key === activeTab,
                onClick: () => dispatch(setDrawerActiveTab(key)),
                dataTestId: `drawer-tab-button-${key}`,
                badge: tabCommentCount > 0 ? tabCommentCount : undefined,
                badgeProps: { 'data-testid': `drawer-tab-count-${key}` },
              }
            })}
          />
        </div>
      )}

      <div className="voice-agents__drawer-body" data-testid="drawer-body">
        {!isCustomView && COMMENTABLE_TABS.includes(activeTab) ? (
          <DrawerCommentsPanel node={node} tabKey={activeTab}>
            {renderTabContent()}
          </DrawerCommentsPanel>
        ) : (
          renderNodeBody(node, renderTabContent)
        )}
      </div>
    </aside>
  )
}

export default NodeDrawer
